import { Button, Checkbox, Textarea } from 'components';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const FeedbackInput = ({
  isHelpful,
  selectedProblems,
  handleProblemChange,
  feedbackNote,
  handleFeedbackCancel,
  handleFeedbackSubmit,
  isSendingFeedback,
  setFeedbackNote,
}: {
  isHelpful: boolean;
  selectedProblems: {
    medicallyIncorrect: boolean;
    missingImportantInfo: boolean;
    wronglyAssigned: boolean;
    tooShort: boolean;
    tooLong: boolean;
  };
  handleProblemChange: (problem: keyof typeof selectedProblems, checked: boolean) => void;
  feedbackNote: string;
  handleFeedbackCancel: () => void;
  handleFeedbackSubmit: () => void;
  isSendingFeedback: boolean;
  setFeedbackNote: (note: string) => void;
}) => {
  const { t } = useTranslation();
  const MAX_FEEDBACK_NOTE_LENGTH = 2000;
  type ProblemKey = keyof typeof selectedProblems;
  const problemOptions = [
    {
      key: 'medicallyIncorrect',
      label: t('result.feedback.problems.medicallyIncorrect', 'Medizinisch falsch'),
    },
    {
      key: 'missingImportantInfo',
      label: t('result.feedback.problems.missingImportantInfo', 'Wichtige Informationen fehlen'),
    },
    {
      key: 'wronglyAssigned',
      label: t(
        'result.feedback.problems.wronglyAssigned',
        'Falsch zugeordnet (z. B. Anamnese, Befund, etc.)'
      ),
    },
    {
      key: 'tooShort',
      label: t('result.feedback.problems.tooShort', 'Inhalte zu stark gekürzt'),
    },
    {
      key: 'tooLong',
      label: t('result.feedback.problems.tooLong', 'Inhalte zu ausführlich'),
    },
  ] satisfies Array<{ key: ProblemKey; label: string }>;

  return (
    <div className="mt-2">
      <div className="flex flex-col items-center">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-full ${
            isHelpful ? 'bg-teal-600' : 'bg-destructive'
          }`}
        >
          {isHelpful ? (
            <ThumbsUp className="h-6 w-6 text-white" />
          ) : (
            <ThumbsDown className="h-6 w-6 text-white" />
          )}
        </div>
      </div>

      {!isHelpful && (
        <div className="mt-6">
          <h3 className="text-xl font-semibold text-foreground">
            {t(
              'result.feedback.problemQuestion',
              'Was war an dieser Zusammenfassung problematisch?'
            )}
          </h3>
          <div className="mt-4 space-y-4">
            {problemOptions.map(({ key, label }) => {
              const isSelected = selectedProblems[key];
              return (
                <div
                  key={key}
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => handleProblemChange(key, !isSelected)}
                  data-ph-no-autocapture
                >
                  <div className="flex items-center gap-3">
                    <Checkbox id={key} checked={isSelected} label={label} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className="text-sm font-medium text-foreground mb-2 block mt-8">
          {t(
            'result.feedback.optionalNoteLabel',
            'Anmerkungen oder Verbesserungsvorschläge (optional)'
          )}
        </label>
        <Textarea
          value={feedbackNote}
          maxLength={MAX_FEEDBACK_NOTE_LENGTH}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFeedbackNote(e.target.value)}
          placeholder={t(
            'result.feedback.optionalNotePlaceholder',
            'Bitte teilen Sie uns weitere Details mit'
          )}
          className="w-full resize-none field-sizing-fixed text-xs"
          rows={4}
        />
      </div>
      <div className="flex flex-col gap-3 mt-6">
        <Button
          size="lg"
          onClick={handleFeedbackSubmit}
          disabled={isSendingFeedback}
          className="w-full"
          data-ph-no-autocapture
        >
          {isSendingFeedback
            ? t('result.feedback.sending', 'Sending...')
            : t('result.feedback.send')}
        </Button>
        <Button
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={handleFeedbackCancel}
          data-ph-no-autocapture
        >
          {t('result.feedback.cancel')}
        </Button>
      </div>
    </div>
  );
};
